import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import loginRegisterBg from '../../assets/images/backgrounds/login_register_bg.png';
import logoBlackText from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import LogoWhite from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import {
  createUser,
  getUser,
  loginUser,
} from '../../utils/api_service/user_api';
import { getImageSrc } from '../../utils/helpers/image-utils';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import styles from './login_register.module.css';
import { validate, validateForm } from './validation/validation_rules';

type UserLandingParams = {
  token: string;
  email: string;
  firstname: string;
  lastname: string;
};

export const UserLanding = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token, email, firstname, lastname } = useParams<UserLandingParams>();
  const [landingInputs, setLandingInputs] = useState<FormInput>({
    email: email,
    firstname: firstname,
    lastname: lastname,
    password: '',
    confirmPassword: '',
    errors: {
      emailError: '',
      passwordError: '',
      confirmPasswordError: '',
    },
  });

  useEffect(() => {
    localStorage.setItem('token', token);
    getUser()
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch user data');
        }
      })
      .then((data) => {
        if (data && data.isVerified) {
          localStorage.clear();
          history.push(`/login/${email}`);
        }
      })
      .catch((error) => {
        toast.error(error.message, { position: 'bottom-center' });
        history.push('/login');
      });
  }, [token, email, history]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let errors = validate(event.target, landingInputs);

    setLandingInputs({
      ...landingInputs,
      [event.target.name]: event.target.value,
      errors: errors,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let isSubmittedFormValid = validateForm(landingInputs);
    if (!isSubmittedFormValid) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      return;
    }

    try {
      localStorage.setItem('token', token);
      const passwordChangeResponse = await createUser(landingInputs.password);
      if (!passwordChangeResponse.ok) {
        throw new Error('Password change failed');
      }

      const loginResponse = await loginUser(landingInputs);
      if (!loginResponse.success) {
        throw new Error('Entered email or password is incorrect or unverified');
      }

      localStorage.setItem('token', loginResponse.accessToken);

      history.push(`/terms-conditions/${loginResponse.accessToken}`);
    } catch (error: any) {
      toast.error(error.message, { position: 'bottom-center' });
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <div className='flex items-start flex-col w-full gap-6 pt-10'>
        <CompanyLogoBadge
            logoUrl={companyLogo}
            companyName={companyName}
            altText='HiAcuity Logo'
        />
        <h1 className='text-2xl md:text-4xl font-medium'>{ t(AUTH_KEYS.userLanding.signUp) }</h1>
      </div>
      <form
          className='flex flex-col space-y-4 mt-10 pb-20'
          onSubmit={handleSubmit}
          data-testid='user-landing-form'
      >
        <TextInput
            type='text'
            label={t(AUTH_KEYS.register.firstname)}
            name='firstname'
            value={landingInputs.firstname || ''}
            onChange={handleChange}
            required={true}
            disabled={true}
        />
        <TextInput
            type='text'
            label={t(AUTH_KEYS.register.lastname)}
            name='lastname'
            value={landingInputs.lastname || ''}
            onChange={handleChange}
            required={true}
            disabled={true}
        />

        <TextInput
            type='email'
            label={t(AUTH_KEYS.register.email)}
            data-testid='email'
            name='email'
            value={landingInputs.email}
            onChange={handleChange}
            disabled={true}
        />
        <div className='flex flex-col space-y-2 '>
          <h1 className='font-medium text-primary-textGray'>{ t(AUTH_KEYS.userLanding.setPassword) }</h1>
          <TextInput
              type='password'
              label={t(AUTH_KEYS.register.password)}
              data-testid='password'
              name='password'
              value={landingInputs.password}
              onChange={handleChange}
          />
          <span className={styles.invalidInput}>
            &nbsp;{ landingInputs.errors.passwordError || '' }
          </span>
          <TextInput
              type='password'
              label={t(AUTH_KEYS.changePassword.confirmPassword)}
              data-testid='password'
              name='confirmPassword'
              value={landingInputs.confirmPassword || ''}
              onChange={handleChange}
          />
          <span className={styles.invalidInput}>
            &nbsp;{ landingInputs.errors.confirmPasswordError || '' }
          </span>
        </div>
        <p className='text-primary-textGray'>
          { t(AUTH_KEYS.userLanding.alreadyHaveAccount) }{ ' ' }
          <a
              href='/login'
              className='text-sm text-primary-dark hover:underline font-semibold cursor-pointer self-end'
          >
            { t(AUTH_KEYS.userLanding.signIn) }
          </a>
        </p>
        <Button
            onClick={() => {}}
            label={t(AUTH_KEYS.userLanding.createAccount)}
            variant='primary'
            type='submit'
        />
      </form>
    </AuthenticationUiLayout>
  );
};

